import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ACCEPT_ALL,
  ESSENTIAL_ONLY,
  clearOptionalStorage,
  getCookieChoice,
  normalizeCookieChoice,
  removeLegacyCookieChoiceStorage,
  setCookieChoice,
} from "../../../utils/cookieConsentUtils";
import { csrfToken } from "../../../utils/csrfTokenUtils";
import { networkService } from "../../config/axios";
import {
  COOKIE_CONSENT_DEFAULT_CONFIG,
} from "../../../constants/cookieConsentDefaults";
import { CONSENT_DEFAULT_COPY, buildConsentSections } from "../../../utils/consentBannerCopy";
import { useViewerCopy } from "../../../utils/viewerLanguage";
import { resolvePrivacyUrl } from "../../../utils/optInGate";
import LanguageSelect from "../../CommonComponents/LanguageSelect";
import "./CookieConsentBanner.css";

const CookieConsentModal = ({
  concentModalStatus,
  setConcentModalStatus,
  customizationLink,
  companyPrivacyUrl,
  sessId,
  pageNumber,
  modalDetails,
  buttonStyles,
}) => {
  const [portalNode, setPortalNode] = useState(null);
  const viewerLanguage = useViewerCopy();
  const consentCopy = viewerLanguage.enabled ? viewerLanguage.copy?.consent : null;
  const copy = { ...CONSENT_DEFAULT_COPY, ...(consentCopy || {}) };

  useEffect(() => {
    removeLegacyCookieChoiceStorage();
  }, []);

  useEffect(() => {
    const node = document.createElement("div");
    node.className = "kz-cookie-portal";
    document.body.appendChild(node);
    setPortalNode(node);
    return () => node.remove();
  }, []);

  const config = useMemo(() => {
    const trimOrFallback = (value, fallback) => {
      if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : fallback;
      }
      return fallback;
    };

    const mergedModal = {
      ...COOKIE_CONSENT_DEFAULT_CONFIG.modal_details,
      ...(modalDetails || {}),
    };

    mergedModal.border_weight =
      Number(mergedModal.border_weight) ||
      COOKIE_CONSENT_DEFAULT_CONFIG.modal_details.border_weight;
    mergedModal.bg_color =
      trimOrFallback(
        mergedModal.bg_color,
        COOKIE_CONSENT_DEFAULT_CONFIG.modal_details.bg_color
      );
    mergedModal.border_color =
      trimOrFallback(
        mergedModal.border_color,
        COOKIE_CONSENT_DEFAULT_CONFIG.modal_details.border_color
      );

    const mergedValue = {
      ...COOKIE_CONSENT_DEFAULT_CONFIG.value,
      ...(buttonStyles || {}),
    };

    Object.keys(mergedValue).forEach((key) => {
      mergedValue[key] = trimOrFallback(mergedValue[key], COOKIE_CONSENT_DEFAULT_CONFIG.value[key]);
    });

    const mergedLink = {
      ...COOKIE_CONSENT_DEFAULT_CONFIG.link_details,
      ...(customizationLink || {}),
    };
    mergedLink.link_text = trimOrFallback(
      mergedLink.link_text,
      COOKIE_CONSENT_DEFAULT_CONFIG.link_details.link_text
    );
    mergedLink.link_url = trimOrFallback(
      mergedLink.link_url,
      companyPrivacyUrl || COOKIE_CONSENT_DEFAULT_CONFIG.link_details.link_url
    );

    return {
      link_details: mergedLink,
      modal_details: mergedModal,
      value: mergedValue,
    };
  }, [customizationLink, modalDetails, buttonStyles]);

  const bannerSections = useMemo(() => {
    if (!concentModalStatus) return [];
    return buildConsentSections({ copy: consentCopy, locale: consentCopy ? viewerLanguage.locale : "en" });
  }, [concentModalStatus, consentCopy, viewerLanguage.locale]);

  const handleCookieConsent = (choice) => {
    const statusKey = normalizeCookieChoice(choice);
    setCookieChoice(statusKey);
    if (statusKey === ESSENTIAL_ONLY) clearOptionalStorage();

    window.dispatchEvent(new CustomEvent("cookieConsentChanged", {
      detail: { cookieStatus: statusKey }
    }));

    if (!sessId) return;

    networkService(`viewer/cookie_consent`, "POST", {
      authenticity_token: csrfToken(),
      cookie_status: statusKey,
      session_id: sessId,
      page: pageNumber,
    }).then((response) => {
      const carriedReceiver = response?.data?.receiver;
      if (carriedReceiver?.id && window.objInit) {
        window.objInit.handoff = { ...(window.objInit.handoff || {}), receiver_id: carriedReceiver.id, receiver_token: carriedReceiver.token };
      }
      window.dispatchEvent(new CustomEvent("cookieConsentSaved", {
        detail: { cookieStatus: statusKey }
      }));
    }).catch((error) => {
      console.error("Failed to save cookie consent:", error);
    });
  };

  const handleClick = (statusKey) => {
    setConcentModalStatus(false);
    handleCookieConsent(statusKey);
  };

  const handleClose = () => {
    if (getCookieChoice()) {
      setConcentModalStatus(false);
      return;
    }
    handleClick(ESSENTIAL_ONLY);
  };

  const closeRef = useRef(handleClose);
  closeRef.current = handleClose;

  useEffect(() => {
    if (!concentModalStatus || !portalNode) return undefined;
    const root = portalNode;

    const previousFocus = document.activeElement;
    const inertTargets = Array.from(document.body.children).filter(
      (node) => node !== root && !node.hasAttribute("inert")
    );
    inertTargets.forEach((node) => node.setAttribute("inert", ""));

    const html = document.documentElement;
    const previousOverflow = { html: html.style.overflow, body: document.body.style.overflow };
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    const focusables = () =>
      Array.from(root.querySelectorAll('button, select, a[href], [tabindex]:not([tabindex="-1"])'));
    root.querySelector(".kz-cookie-dialog__btn")?.focus();

    const handleKeyDown = (event) => {
      event.stopPropagation();
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!root.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      inertTargets.forEach((node) => node.removeAttribute("inert"));
      html.style.overflow = previousOverflow.html;
      document.body.style.overflow = previousOverflow.body;
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    };
  }, [concentModalStatus, portalNode]);

  if (!concentModalStatus || !portalNode) return null;

  const borderWeight = config.modal_details.border_weight || 0;
  const buttonStyle = {
    background: config.value.btn2_bg_color,
    color: config.value.btn2_text_color,
    border: `1px solid ${config.value.btn2_border_color}`,
  };

  return createPortal(
    <div className="kz-cookie-scrim">
      <div
        className="kz-cookie-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kz-cookie-dialog-title"
        aria-describedby="kz-cookie-dialog-text"
        dir={consentCopy ? viewerLanguage.dir : undefined}
        lang={consentCopy ? viewerLanguage.locale : undefined}
        style={{
          border: `${borderWeight}px solid ${config.modal_details.border_color}`,
          background: config.modal_details.bg_color,
        }}
      >
        <div className="kz-cookie-dialog__top">
          {consentCopy && (
            <div className="kz-language-corner">
              <LanguageSelect
                languages={viewerLanguage.languages}
                value={viewerLanguage.locale}
                onChange={viewerLanguage.setLocale}
                label={viewerLanguage.copy?.language_label}
              />
            </div>
          )}
          <button
            type="button"
            className="kz-cookie-dialog__close"
            aria-label={copy.close}
            title={copy.close}
            onClick={handleClose}
          >
            ×
          </button>
        </div>
        <h2 id="kz-cookie-dialog-title" className="kz-cookie-dialog__title">
          {copy.title}
        </h2>

        <div id="kz-cookie-dialog-text" className="kz-cookie-dialog__text">
          {bannerSections.map((section) => (
            <p key={section.key}>
              {section.label && <strong>{section.label}</strong>}
              {section.label && section.separator}
              {section.text}
            </p>
          ))}
          {window?.objInit?.gpc === true && (
            <p>{copy.gpc_notice}</p>
          )}
        </div>

        <div className="kz-cookie-dialog__actions">
          <button
            type="button"
            className="kz-cookie-dialog__btn"
            style={buttonStyle}
            onClick={() => handleClick(ESSENTIAL_ONLY)}
          >
            {copy.essential_only}
          </button>
          <button
            type="button"
            className="kz-cookie-dialog__btn"
            style={buttonStyle}
            onClick={() => handleClick(ACCEPT_ALL)}
          >
            {copy.accept_all}
          </button>
        </div>

        <p className="kz-cookie-dialog__footer">
          <a
            className="privacy"
            target="_blank"
            rel="noreferrer"
            href={consentCopy ? resolvePrivacyUrl(companyPrivacyUrl) : (companyPrivacyUrl || config.link_details.link_url || "https://kinetizine.com/privacy")}
          >
            {copy.privacy_notice}
          </a>
          {" · "}{copy.withdraw}
        </p>
      </div>
    </div>,
    portalNode
  );
};

export default CookieConsentModal;
